/**
 * CourseModal Component
 * Enterprise-grade reusable modal for displaying course details
 */

import React, { useEffect, useState } from 'react';
import { X, Star, Users, Clock, BookOpen, Sparkles, TrendingUp, AlertCircle, Award, Target, Zap } from 'lucide-react';
import { Course } from '@/types/api.types';
import { courseService } from '@/services/course.service';

interface CourseModalProps {
  courseId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onEnroll?: (courseId: string) => void;
}

export const CourseModal: React.FC<CourseModalProps> = ({ 
  courseId, 
  isOpen, 
  onClose, 
  onEnroll 
}) => {
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && courseId) {
      fetchCourseData();
    }
  }, [isOpen, courseId]);

  const fetchCourseData = async () => {
    if (!courseId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await courseService.getCourseById(courseId);
      
      if (response.success && response.data) {
        setCourse(response.data);
      } else {
        setError('Course not found');
      }
    } catch (err) {
      console.error('Error fetching course data:', err);
      setError('Failed to load course details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = () => {
    if (course && onEnroll) {
      onEnroll(course.id);
    }
  };

  const handleClose = () => {
    setCourse(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const savingsAmount = course?.full_price && course?.discount_price 
    ? course.full_price - course.discount_price 
    : 0;
  
  const savingsPercentage = course?.full_price && course?.discount_price
    ? Math.round((savingsAmount / course.full_price) * 100)
    : 0;

  const getCategoryGradient = (category?: string) => {
    switch (category) {
      case 'Core Skills':
        return 'from-violet-600 via-purple-600 to-fuchsia-600';
      case 'Entrepreneurship':
        return 'from-blue-600 via-cyan-600 to-teal-600';
      case 'AI Money Making Tracks':
        return 'from-orange-600 via-amber-600 to-yellow-600';
      default:
        return 'from-purple-600 to-blue-600';
    }
  };

  const getCategoryIcon = (category?: string) => {
    switch (category) {
      case 'Core Skills':
        return <BookOpen className="w-5 h-5" />;
      case 'Entrepreneurship':
        return <Target className="w-5 h-5" />;
      case 'AI Money Making Tracks':
        return <TrendingUp className="w-5 h-5" />;
      default:
        return <Sparkles className="w-5 h-5" />;
    }
  };

  const gradient = getCategoryGradient(course?.category);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div 
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-900 to-black rounded-3xl border border-white/20 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-6 right-6 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-all hover:rotate-90 duration-300"
          aria-label="Close modal"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center p-20">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
              <p className="text-gray-400">Loading course details...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="flex items-center justify-center p-20">
            <div className="flex flex-col items-center space-y-4 max-w-md">
              <AlertCircle className="w-16 h-16 text-red-400" />
              <p className="text-red-400 text-center">{error}</p>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 rounded-full transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        {course && !loading && !error && (
          <>
            {/* Header Section */}
            <div className="relative p-8 pb-6 border-b border-white/10">
              <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-10`}></div>
              <div className="relative">
                {/* Category Badge */}
                {course.category && (
                  <div className={`inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r ${gradient} rounded-full mb-4`}>
                    {getCategoryIcon(course.category)}
                    <span className="text-sm font-semibold">{course.category}</span>
                  </div>
                )}

                <h2 className="text-4xl font-bold mb-3 bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  {course.title}
                </h2>
                
                <p className="text-gray-400 text-lg leading-relaxed">
                  {course.description}
                </p>

                {/* Stats */}
                <div className="flex items-center gap-6 mt-6 flex-wrap">
                  {course.skill_level && (
                    <div className="flex items-center space-x-2">
                      <Award className="w-5 h-5 text-purple-400" />
                      <span className="text-sm text-gray-300">{course.skill_level}</span>
                    </div>
                  )}
                  
                  {course.duration_hours && (
                    <div className="flex items-center space-x-2">
                      <Clock className="w-5 h-5 text-blue-400" />
                      <span className="text-sm text-gray-300">{course.duration_hours} hours</span>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2">
                    <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    <span className="text-sm text-gray-300">Quality Guaranteed</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5 text-green-400" />
                    <span className="text-sm text-gray-300">Lifetime Access</span>
                  </div>
                </div>
              </div>
            </div>

            {/* What You'll Learn */}
            <div className="p-8">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
                <Zap className="w-6 h-6 text-yellow-400" />
                <span>What You'll Learn</span>
              </h3>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start space-x-3 p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className={`flex-shrink-0 w-8 h-8 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center`}>
                    <span className="text-sm font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Master Core Concepts</h4>
                    <p className="text-sm text-gray-400">Build a solid foundation in AI and automation</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className={`flex-shrink-0 w-8 h-8 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center`}>
                    <span className="text-sm font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Hands-On Projects</h4>
                    <p className="text-sm text-gray-400">Apply your knowledge with real-world exercises</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className={`flex-shrink-0 w-8 h-8 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center`}>
                    <span className="text-sm font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Industry Best Practices</h4>
                    <p className="text-sm text-gray-400">Learn from experts with proven experience</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className={`flex-shrink-0 w-8 h-8 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center`}>
                    <span className="text-sm font-bold">4</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Career Advancement</h4>
                    <p className="text-sm text-gray-400">Gain skills that employers are actively seeking</p>
                  </div>
                </div>
              </div>

              {/* Course Features */}
              <div className="bg-gradient-to-br from-white/5 to-white/[0.02] rounded-2xl p-6 border border-white/10 mb-8">
                <h4 className="font-semibold text-lg text-white mb-4 flex items-center space-x-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  <span>Course Features</span>
                </h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 bg-gradient-to-r ${gradient} rounded-full`}></div>
                    <span className="text-sm text-gray-300">On-demand video content</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 bg-gradient-to-r ${gradient} rounded-full`}></div>
                    <span className="text-sm text-gray-300">Downloadable resources</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 bg-gradient-to-r ${gradient} rounded-full`}></div>
                    <span className="text-sm text-gray-300">Certificate of completion</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 bg-gradient-to-r ${gradient} rounded-full`}></div>
                    <span className="text-sm text-gray-300">Community forum access</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 bg-gradient-to-r ${gradient} rounded-full`}></div>
                    <span className="text-sm text-gray-300">Mobile & desktop access</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 bg-gradient-to-r ${gradient} rounded-full`}></div>
                    <span className="text-sm text-gray-300">Lifetime updates</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer with Pricing */}
            <div className="sticky bottom-0 p-8 pt-6 border-t border-white/10 bg-gradient-to-t from-gray-900 to-transparent backdrop-blur-sm">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="flex items-baseline gap-3 mb-1">
                    <span className={`text-4xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
                      ${course.discount_price || course.full_price}
                    </span>
                    {course.discount_price && course.discount_price < course.full_price && (
                      <span className="text-xl text-gray-500 line-through">
                        ${course.full_price}
                      </span>
                    )}
                  </div>
                  {savingsAmount > 0 && (
                    <p className="text-sm text-green-400">
                      Save {savingsPercentage}% • ${savingsAmount.toFixed(2)} off
                    </p>
                  )}
                </div>

                <button
                  onClick={handleEnroll}
                  className={`px-10 py-4 bg-gradient-to-r ${gradient} rounded-full font-bold text-lg hover:shadow-2xl transition-all hover:scale-105 flex items-center space-x-2 shadow-lg`}
                  style={{ boxShadow: `0 20px 40px -12px rgba(139, 92, 246, 0.3)` }}
                >
                  <span>Enroll Now</span>
                  <Sparkles className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CourseModal;
